//#region import
import React, { Component } from "react"
import { bindActionCreators } from "redux"
import { connect } from "react-redux"
import { Prompt } from "react-router"

import { actions as appActions } from "../../modules/appModule"
import { actions as userActions } from "../../modules/userModule"

// import components
import Page, { Content } from "../../components/Page"
import DashboardNavbar from "../../components/layout/DashboardNavbar"
import Footer from "../../components/Footer"
import MonitorSearchModal from "../../components/MonitorSearchModal"

import { COLOR, JARGON, KEY_VALUES, MONITORING } from "../../modules/constants"

import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Switch,
  TextField,
  Tooltip,
  Typography
} from "@mui/material"

class MonitoringConfigPage extends Component {

  constructor(props) {
    // console.log("MonitoringConfigPage constructor: props: ", props)
    props.user.getUserMonitoringConfig()
    super()

    if (props.userMonitoring.Categories === null)
      this.state = {
        searchTerms: Array(MONITORING.NUM_SEARCH_TERMS).fill(''),
        analysePrompt: '',
        synopsisPrompt: '',
        searching: false,
      }
    else
      this.state = {
        searchTerms: props.userMonitoring.SearchTerms,
        analysePrompt: props.userMonitoring.AnalysePrompt || '',
        synopsisPrompt: props.userMonitoring.SynopsisPrompt || '',
      }
  }

  // Ticks once a second so the Test button's countdown label / disabled state stay live while a
  // cooldown (see getTestRemainingMs) is in progress. forceUpdate is used deliberately - this
  // ticks off a ref, not props/state, so shouldComponentUpdate (which only reacts to prop/state
  // changes) would otherwise block the re-render.
  componentDidMount() {
    this.testCountdownInterval = setInterval(() => {
      if (this.getTestRemainingMs() > 0 || this.wasCountingDown) {
        this.wasCountingDown = this.getTestRemainingMs() > 0
        this.forceUpdate()
      }
    }, 1000)
  }

  componentWillUnmount() {
    clearInterval(this.testCountdownInterval)
  }

  // How long, in ms, until the Test button's cooldown (since userMonitoring.LastTestRun,
  // set both optimistically on click - see userModule.js - and authoritatively by the backend) expires
  getTestRemainingMs = () => {
    const { LastTestRun } = this.props.userMonitoring
    if (!LastTestRun) return 0
    return Math.max(0, MONITORING.TEST_COOLDOWN_MS - (Date.now() - new Date(LastTestRun).getTime()))
  }

  shouldComponentUpdate(nextProps) {
    if (this.props.userMonitoring.Categories === null) {

      if (nextProps.userMonitoring.Categories !== this.props.userMonitoring.Categories)
        this.setState({
          searchTerms: nextProps.userMonitoring.SearchTerms,
          analysePrompt: nextProps.userMonitoring.AnalysePrompt || '',
          synopsisPrompt: nextProps.userMonitoring.SynopsisPrompt || '',
        })

      return false
    }
    else {
      return true
    }
  }

  render() {
    // console.log('MonitoringConfigPage props:', this.props)
    const { userMonitoring } = this.props

    const disabled = !userMonitoring.Enabled

    // Set valuesAreDirty: true if any local value differs from the global values
    const valuesAreDirty = userMonitoring.SearchTerms.some((value, index) => value !== this.state.searchTerms[index])

    const analysePromptDirty = (userMonitoring.AnalysePrompt || '') !== this.state.analysePrompt
    const synopsisPromptDirty = (userMonitoring.SynopsisPrompt || '') !== this.state.synopsisPrompt

    const handleSwitch = event => {
      // console.log('event.target:', event.target)
      let userMonitoringClone = JSON.parse(JSON.stringify(userMonitoring))
      userMonitoringClone.Enabled = event.target.checked
      this.props.user.setUserMonitoringConfig(userMonitoringClone)
    }

    const handleTest = () => {
      this.props.user.monitoringTest({ results: null, test: true })
      this.setState({ searching: true })
    }

    // Reasons for the Test button being disabled, most relevant/actionable first - the message
    // shown in its tooltip. Returns "" (no tooltip) once none apply, ie the button is enabled.
    const getTestButtonTooltip = (testRemainingMs) => {
      if (testRemainingMs > 0)
        return `Please wait ${formatCountdown(testRemainingMs)} before testing again`
      if (this.props.userMonitoringSearching)
        return `A test is already running`
      if (disabled)
        return `Enable monitoring above to run a test`
      if (valuesAreDirty || analysePromptDirty || synopsisPromptDirty)
        return `Save your changes before running a test`
      if (configAllFalse())
        return `Select at least one group above to run a test`
      if (searchTermsAllEmpty())
        return `Enter at least one search term to run a test`
      return ``
    }

    const switchControl = ({ key }) => {
      // console.log('userNewsAlerts[key]:', userNewsAlerts[key])
      const testRemainingMs = this.getTestRemainingMs()
      const testButtonDisabled = disabled || configAllFalse() || searchTermsAllEmpty() || this.props.userMonitoringSearching || valuesAreDirty || analysePromptDirty || synopsisPromptDirty || testRemainingMs > 0
      const testButtonLabel = testRemainingMs > 0 ? formatCountdown(testRemainingMs) : `test`

      return (
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          height={"auto"}
          sx={{ mb: 1 }}
        >
          <FormControlLabel
            sx={{ ml: 0 }}
            checked={userMonitoring.Enabled}
            name={key}
            control={<Switch />}
            label={<Typography style={{ fontSize: 20, fontWeight: "bold", }}>{key}</Typography>}
            onChange={handleSwitch}
            labelPlacement="start"

          />
          {button({ disabled: testButtonDisabled, buttonLabel: testButtonLabel, handleOnClick: handleTest, style: {}, tooltip: testButtonDisabled ? getTestButtonTooltip(testRemainingMs) : `` })}
        </Box>
      )
    }

    const handleChange = event => {
      // console.log('event.target:', event.target)
      let userMonitoringClone = JSON.parse(JSON.stringify(userMonitoring))
      userMonitoringClone.Categories[event.target.name] = event.target.checked
      this.props.user.setUserMonitoringConfig(userMonitoringClone)
    }

    const handleAnalyseSwitch = event => {
      let userMonitoringClone = JSON.parse(JSON.stringify(userMonitoring))
      userMonitoringClone.AnalyseEnabledOnTest = event.target.checked
      this.props.user.setUserMonitoringConfig(userMonitoringClone)
    }

    const handleSynopsisSwitch = event => {
      let userMonitoringClone = JSON.parse(JSON.stringify(userMonitoring))
      userMonitoringClone.SynopsisEnabledOnTest = event.target.checked
      this.props.user.setUserMonitoringConfig(userMonitoringClone)
    }

    const handleForceAnalyseErrorChange = event => {
      let userMonitoringClone = JSON.parse(JSON.stringify(userMonitoring))
      userMonitoringClone.ForceAnalyseError = event.target.checked
      this.props.user.setUserMonitoringConfig(userMonitoringClone)
    }

    const handleForceSynopsisErrorChange = event => {
      let userMonitoringClone = JSON.parse(JSON.stringify(userMonitoring))
      userMonitoringClone.ForceSynopsisError = event.target.checked
      this.props.user.setUserMonitoringConfig(userMonitoringClone)
    }

    const handleAnalysePromptChange = event => {
      this.setState({ analysePrompt: event.target.value })
    }

    const handleAnalysePromptSave = () => {
      let userMonitoringClone = JSON.parse(JSON.stringify(userMonitoring))
      userMonitoringClone.AnalysePrompt = this.state.analysePrompt
      this.props.user.setUserMonitoringConfig(userMonitoringClone)
    }

    const handleAnalysePromptUndo = () => {
      this.setState({ analysePrompt: userMonitoring.AnalysePrompt || '' })
    }

    const handleSynopsisPromptChange = event => {
      this.setState({ synopsisPrompt: event.target.value })
    }

    const handleSynopsisPromptSave = () => {
      let userMonitoringClone = JSON.parse(JSON.stringify(userMonitoring))
      userMonitoringClone.SynopsisPrompt = this.state.synopsisPrompt
      this.props.user.setUserMonitoringConfig(userMonitoringClone)
    }

    const handleSynopsisPromptUndo = () => {
      this.setState({ synopsisPrompt: userMonitoring.SynopsisPrompt || '' })
    }

    // Dev-only: lets a developer edit the two prompts that drive Gemini analysis - Analyse prompt
    // (run per-release, for relevance probability + reason) and Synopsis prompt (run once on the
    // full batch, for an overall summary paragraph) - ahead of this being rolled out more broadly.
    // Both prompts are always used on a real scheduled run regardless of the switches below (a
    // scheduled run always analyses and builds a synopsis - see Monitoring.MonitoringAsync); the
    // switches only control whether a TEST run ('Test' button) includes each one, so a developer
    // can preview the search alone without waiting on/paying for a Gemini call every click.
    const analyseSection = () => (
      <React.Fragment>
        <Box sx={{ mt: 2 }}>
          <Box display={"flex"} justifyContent={"space-between"} height={"auto"}>
            <FormControlLabel
              sx={{ ml: 0 }}
              checked={!!userMonitoring.AnalyseEnabledOnTest}
              name="AnalyseEnabledOnTest"
              control={<Switch />}
              label={<Typography style={{ fontSize: 20, fontWeight: "bold" }}>Analyse prompt</Typography>}
              onChange={handleAnalyseSwitch}
              labelPlacement="start"
              disabled={disabled}
            />
          </Box>

          {/* Dev-only debug toggle: forces every per-release Analyse call on the next Test run to
              fail (503), so the AiError handling (email + MonitorSearchModal.jsx) can be exercised
              on demand, independently of the Synopsis toggle below. Only affects Test - a real
              scheduled run always uses the real Gemini call regardless of this setting (see
              Monitoring.MonitoringAsync). */}
          <FormControlLabel
            sx={{ ml: 0 }}
            checked={!!userMonitoring.ForceAnalyseError}
            name="ForceAnalyseError"
            control={<Checkbox />}
            label="Force Analyse calls to fail (503) on Test"
            onChange={handleForceAnalyseErrorChange}
            disabled={disabled}
          />

          <Box sx={{ mt: 1 }} display={"flex"} justifyContent={"space-between"}>
            {button({ disabled: disabled || !analysePromptDirty, handleOnClick: handleAnalysePromptSave, buttonLabel: `save changes`, style: {} })}
            {button({ disabled: disabled || !analysePromptDirty, handleOnClick: handleAnalysePromptUndo, buttonLabel: `undo changes`, style: {} })}
          </Box>

          <Box sx={{ mt: 1 }}>
            <TextField
              disabled={disabled}
              value={this.state.analysePrompt}
              onChange={handleAnalysePromptChange}
              label="Analyse prompt"
              fullWidth
              multiline
              minRows={4}
            />
          </Box>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Box display={"flex"} justifyContent={"space-between"} height={"auto"}>
            <FormControlLabel
              sx={{ ml: 0 }}
              checked={!!userMonitoring.SynopsisEnabledOnTest}
              name="SynopsisEnabledOnTest"
              control={<Switch />}
              label={<Typography style={{ fontSize: 20, fontWeight: "bold" }}>Synopsis prompt</Typography>}
              onChange={handleSynopsisSwitch}
              labelPlacement="start"
              disabled={disabled}
            />
          </Box>

          {/* Dev-only debug toggle: forces the batch Synopsis call on the next Test run to fail
              (503), so the synopsis-failure handling (email + MonitorSearchModal.jsx) can be
              exercised on demand, independently of the Analyse toggle above. See that toggle's
              comment for the TEST-run-only scoping. */}
          <FormControlLabel
            sx={{ ml: 0 }}
            checked={!!userMonitoring.ForceSynopsisError}
            name="ForceSynopsisError"
            control={<Checkbox />}
            label="Force Synopsis calls to fail (503) on Test"
            onChange={handleForceSynopsisErrorChange}
            disabled={disabled}
          />

          <Box sx={{ mt: 1 }} display={"flex"} justifyContent={"space-between"}>
            {button({ disabled: disabled || !synopsisPromptDirty, handleOnClick: handleSynopsisPromptSave, buttonLabel: `save changes`, style: {} })}
            {button({ disabled: disabled || !synopsisPromptDirty, handleOnClick: handleSynopsisPromptUndo, buttonLabel: `undo changes`, style: {} })}
          </Box>

          <Box sx={{ mt: 1 }}>
            <TextField
              disabled={disabled}
              value={this.state.synopsisPrompt}
              onChange={handleSynopsisPromptChange}
              label="Synopsis prompt"
              fullWidth
              multiline
              minRows={4}
            />
          </Box>
        </Box>
      </React.Fragment>
    )

    // const mapObject = ({ disabled, obj, style, tip }) => Object.keys(obj).map((key, index) => (
    const mapObject = ({ obj, style, tip }) => Object.keys(obj).map((key, index) => (
      (typeof obj[key] !== "function" && <Tooltip
        key={index}
        title={tip[key]}
        placement="top"
        disableFocusListener
        enterDelay={KEY_VALUES.TOOLTIP_DELAY}
      >
        <FormControlLabel
          style={style}
          control={
            <Checkbox
              disabled={disabled}
              checked={userMonitoring.Categories[obj[key]]}
              onChange={handleChange}
              name={obj[key]}
              style={style}
              id={`${obj.getName()}.${key}`}
            />}
          key={index}
          label={obj[key]}
        />
      </Tooltip>)
    ))

    const checkboxFormGroup = ({ obj, title, tip }) => {
      const style = { color: disabled ? COLOR.DISABLED : getColor() }
      return (
        <React.Fragment>
          <br />
          <div style={{ fontWeight: "bold" }} >
            {title}
            <FormGroup className={"checkbox"} style={{ ...style }} >
              {mapObject({
                obj,
                style,
                tip,
              })}
            </FormGroup>
          </div>
        </React.Fragment>
      )
    }

    const textFields = ({ title, values }) => {

      // Handle changes to the search terms
      const handleChange = (event, index) => {
        // console.log('event:', event)
        if (event.nativeEvent.inputType !== "insertLineBreak") {
          const newValues = [...this.state.searchTerms]
          newValues[index] = event.target.value

          if (this.props.features.Developer && newValues[0] === "defaults") {
            // Set the default searchTerms for testing
            var searchTerms = Array(MONITORING.NUM_SEARCH_TERMS).fill('')
            searchTerms.splice(0, defaultSearchTerms.length, ...defaultSearchTerms)
            this.setState({
              searchTerms,
            })
          }
          else if (newValues[0] === "reset")
            // Clear all the search terms
            this.setState({
              searchTerms: Array(MONITORING.NUM_SEARCH_TERMS).fill(''),
            })
          else
            this.setState({
              searchTerms: newValues,
            })
        } else
          // save if "enter" pressed
          handleSave()
      }

      // Save changes to the search terms
      const handleSave = () => {
        // console.log(this.state.searchTerms)
        this.props.user.saveSearchTerms({ searchTerms: this.state.searchTerms, userMonitoring })

        // Force a update. We shouldn't need this because saveSearchTerms should result in update??
        this.setState({
          searchTerms: this.state.searchTerms,
        })
      }

      // Undo any changes to the search terms
      const handleUndo = () => {
        this.setState({
          searchTerms: this.props.userMonitoring.SearchTerms,
        })
      }

      return (
        <Box
          component="form"
          sx={{ marginTop: 5, '& .MuiTextField-root': { mt: 1 } }}
          noValidate
          autoComplete="off"
        >

          <Prompt
            when={valuesAreDirty}
            message={JARGON.UNSAVED_CHANGES}
          />

          <div style={{ fontWeight: "bold" }} >
            {title}
          </div>

          <Box
            sx={{ mt: 1 }}
            display={"flex"}
            justifyContent={"space-between"}
          >
            {button({ disabled: disabled || !valuesAreDirty, handleOnClick: handleSave, buttonLabel: `save changes`, style: {} })}
            {button({ disabled: disabled || !valuesAreDirty, handleOnClick: handleUndo, buttonLabel: `undo changes`, style: {} })}
          </Box>

          <Box
            sx={{ mt: 1 }}>
            {values.map((value, index) => (
              <TextField
                sx={{
                  '& label': {
                    color: COLOR.BLACK,
                  },
                  '& label.Mui-focused': {
                    color: COLOR.BRAND.STD,
                  },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: COLOR.LIGHT_GREY,
                    },
                    '&:hover fieldset': {
                      borderColor: 'black',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: COLOR.BRAND.STD,
                    },
                  },
                }}
                key={index}
                disabled={disabled}
                value={value}
                onChange={(event) => handleChange(event, index)}
                label={`${index + 1}`}
                fullWidth
                multiline
              />
            ))}
          </Box>
        </Box>
      )
    }

    const button = ({ disabled = false, buttonLabel, style, height = "auto", handleOnClick = () => console.log(buttonLabel), tooltip = "" }) => {
      const buttonEl =
        <Button
          sx={{
            ...style,
            height,
            width: "auto",
            marginLeft: "0",
            color: "white",
            background: COLOR.BRAND.STD,
            "&:hover": {
              background: COLOR.BRAND.STD,
              filter: "brightness(0.75)",
            },
          }}
          className="submitButton"
          variant="contained"
          // color="primary"
          size="medium"
          onClick={e => handleOnClick()}
          disabled={disabled}
        >
          {buttonLabel}
        </Button>

      // A disabled native button doesn't fire the mouse events a Tooltip listens for, so it's
      // wrapped in a span (MUI's documented workaround) whenever there's a tooltip to show
      return tooltip
        ? <Tooltip title={tooltip}><span>{buttonEl}</span></Tooltip>
        : buttonEl
    }

    // Return true if all alerts in group userMonitoring.Categories set to false
    const configAllFalse = () => {
      for (const key in MONITORING.FTSE_INDEX)
        if (userMonitoring.Categories[MONITORING.FTSE_INDEX[key]])
          return false

      return true
    }

    // Returns true if all the search terms are empty
    const searchTermsAllEmpty = () => userMonitoring.SearchTerms.every((value, index) => value === "")

    // Return COLOR.BAD if all NEWS_CATEGORY and all FTSE_INDEX are unchecked (false)
    const getColor = () => configAllFalse()
      ? COLOR.BAD
      : COLOR.BLACK

    return (
      <React.Fragment>
        <DashboardNavbar backLink portrait={this.props.portrait} />

        <Page>
          {userMonitoring.Categories && <Content scroll>
            <div className="form-page">
              <div className="form-cell">
                <div className="form-wrapper" style={{ width: 600 }}>
                  {switchControl({ key: MONITORING.DISCLOSURES })}
                  {this.props.features.Developer && analyseSection()}
                  {checkboxFormGroup({ obj: MONITORING.FTSE_INDEX, title: MONITORING.FTSE_INDEX_INTRO, tip: MONITORING.FTSE_INDEX_TIP })}
                  {textFields({ title: MONITORING.SEARCH_TERMS_INTRO, values: this.state.searchTerms })}
                </div>
              </div>
            </div>
          </Content>}
        </Page >

        <
          // @ts-ignore
          Footer urlSource={this.props.urlSource} />

        <MonitorSearchModal
          open={this.state.searching === true}
          onClose={() => { this.setState({ searching: false }) }}
          results={this.props.userMonitoringResults}
          error={this.props.userMonitoringError}
          maxDocumentsExceeded={this.props.userMonitoringMaxDocumentsExceeded}
          synopsis={this.props.userMonitoringSynopsis}
          geminiUsage={this.props.userMonitoringGeminiUsage}
        />

      </React.Fragment >

    )
  }
}

const mapStateToProps = ({ app, common, user }) => {
  return {
    // app
    features: app.features,
    rollups: app.rollups,

    // common
    urlSource: common.urlSource,

    // user
    portrait: user.portrait,
    userAlerts: user.userAlerts,
    userMonitoring: user.userMonitoring,
    userMonitoringSearching: user.userMonitoringSearching,
    userMonitoringResults: user.userMonitoringResults,
    userMonitoringError: user.userMonitoringError,
    userMonitoringMaxDocumentsExceeded: user.userMonitoringMaxDocumentsExceeded,
    userMonitoringSynopsis: user.userMonitoringSynopsis,
    userMonitoringGeminiUsage: user.userMonitoringGeminiUsage,
  }
}

const mapDispatchToProps = dispatch => {
  return {
    app: bindActionCreators(appActions, dispatch),
    user: bindActionCreators(userActions, dispatch),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(MonitoringConfigPage)

// Formats a remaining ms count as the Test button's countdown label, eg "2:59"
const formatCountdown = ms => {
  const totalSeconds = Math.ceil(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

const defaultSearchTerms = [
  "asset divestiture",
  "asset monetisation",
  "asset sale",
  "business separation",
  "business unit optimisation",
  "capital reallocation",
  "corporate restructuring",
  "de-merger",
  "disposition process",
  "divestiture",
  "divestment strategy",
  "exit strategy",
  "held for sale",
  "non-core business unit",
  "non-core division",
  "non-strategic asset sale",
  "operational refocus",
  "portfolio optimisation",
  "portfolio reorganisation",
  "potential disposal",
  "rationalisation",
  "simplification of the business",
  "spin-off",
  "strategic alternatives",
  "strategic options",
  "strategic review",
  "streamlining operations",
  "unlocking value",
  "value creation opportunities",
]

